import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FlashRepository } from 'repository/flashRepository';
import { MeetingRepository } from 'repository/meetingRepository';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import { openShare, copyToClipboard } from 'common/utils/share';
import { InviteFromPastMeetings } from 'components/meeting/InviteFromPastMeetings';
import { MapContainer } from 'components/map';
import { useUserInfo } from 'hooks/useAuth';
import {
    AppScreen, Badge, Bleed, Button, ButtonFooter, PageIntro, Section, SectionHeader,
} from 'components/ds';
import styles from './Flash.module.css';

interface InvitedMember {
    userNo: number;
    nickname: string;
}

/**
 * 번개 생성 직후 공유 화면
 */
export const FlashShare = () => {
    const navigate = useNavigate();
    const { token } = useParams();
    const location = useLocation();
    const { t } = useTranslation();
    const { userNo } = useUserInfo();
    const [data, setData] = useState<any>(null);
    const [toastMsg, setToastMsg] = useState('');
    const [inviteOpen, setInviteOpen] = useState(false);
    const [invitedMembers, setInvitedMembers] = useState<InvitedMember[]>([]);

    // FlashCreate 에서 생성 직후 초대한 사람들의 userNo 목록 (navigation state)
    const invitedUserNos: number[] = location.state?.invitedUserNos || [];

    useEffect(() => {
        if (!token) return;
        FlashRepository.onGetByToken(token)
            .then(res => setData(res?.data?.data || res?.data))
            .catch(() => {});
    }, [token]);

    // 초대한 사람 이름 표시 — 자주 함께한 사람 목록에서 userNo 매칭 (FlashCreate 선택 출처와 동일)
    useEffect(() => {
        if (!invitedUserNos.length) return;
        MeetingRepository.onGetFrequentCoMembersByUser(50)
            .then((res: any) => {
                const list = res?.data?.data || res?.data || [];
                const wanted = new Set(invitedUserNos.map(Number));
                const matched = (Array.isArray(list) ? list : [])
                    .filter((r: any) => wanted.has(Number(r.userNo)))
                    .map((r: any) => ({
                        userNo: Number(r.userNo),
                        nickname: (() => {
                            try { return decodeURIComponent(r.nickname || ''); } catch { return r.nickname || ''; }
                        })(),
                    }));
                setInvitedMembers(matched);
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shareUrl = `${window.location.origin}/flash/vote/${token}`;

    const copyLink = async () => {
        const ok = await copyToClipboard(shareUrl);
        setToastMsg(ok ? t('flash.share.copied') : t('flash.share.copyFailed'));
        setTimeout(() => setToastMsg(''), 2500);
    };

    const safeDecode = (s?: string): string => {
        if (!s) return '';
        try { return decodeURIComponent(s); } catch { return s; }
    };

    const nativeShare = async () => {
        // 네이티브 앱 / 모바일 웹 Web Share API / 데스크톱 클립보드 폴백 — 모임 공유와 동일한 헬퍼 사용
        const title = safeDecode(data?.title) || '';
        const creator = safeDecode(data?.creatorNickname) || '';
        const startStr = data?.startDatetime
            ? new Date(data.startDatetime).toLocaleString(undefined, { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            : '';
        // i18n 풀에서 인트로/클로징 랜덤 선택
        const intros = (t('tone.shareFlash.intro', { returnObjects: true }) as any) as string[] | undefined;
        const closings = (t('tone.shareFlash.closing', { returnObjects: true }) as any) as string[] | undefined;
        const introArr = Array.isArray(intros) ? intros : [`${creator}`];
        const closingArr = Array.isArray(closings) ? closings : [''];
        const intro = introArr[Math.floor(Math.random() * introArr.length)].replace(/\{\{creator\}\}/g, creator);
        const closing = closingArr[Math.floor(Math.random() * closingArr.length)];
        const text = `${intro}\n"${title}"${startStr ? `\n${startStr}` : ''}${closing}`;
        await openShare({ title, text, url: shareUrl });
    };

    // 후보 장소 — 좌표 유효한 것만 지도 마커로
    const candidates: any[] = data?.candidates || [];
    const candMarkers = candidates
        .map((c: any, i: number) => ({ raw: c, idx: i, lat: Number(c.latitude), lng: Number(c.longitude) }))
        .filter(c => Number.isFinite(c.lat) && Number.isFinite(c.lng) && (c.lat !== 0 || c.lng !== 0));

    return (
        <AppScreen title={t('flash.header.share')} bar="sub" hasFooter>
            <PageIntro
                title={t('flash.share.celebrate')}
                desc={data ? safeDecode(data.title) : undefined}
            />

            {/* 후보 장소 */}
            {candidates.length > 0 && (
                <Section spacing="tight">
                    <SectionHeader
                        title={t('flash.candidatePlace')}
                        count={candidates.length}
                    />
                    <ul className={styles.cands}>
                        {candidates.map((c: any, i: number) => (
                            <li key={c.candidateId || i} className={styles.cand}>
                                <span className={styles.candBody}>
                                    <span className={styles.candName}>{safeDecode(c.placeName)}</span>
                                    {c.placeAddress && (
                                        <span className={styles.slotAddr}>{safeDecode(c.placeAddress)}</span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>

                    {candMarkers.length > 0 && (
                        <Bleed>
                            <MapContainer
                                center={{ lat: candMarkers[0].lat, lng: candMarkers[0].lng }}
                                markers={candMarkers.map(c => ({
                                    id: c.raw.candidateId || c.idx,
                                    lat: c.lat,
                                    lng: c.lng,
                                    label: String(c.idx + 1),
                                    tooltip: safeDecode(c.raw.placeName),
                                }))}
                                height={200}
                                fitBounds={candMarkers.length > 1}
                                placeName={safeDecode(candMarkers[0].raw.placeName)}
                                placeAddress={safeDecode(candMarkers[0].raw.placeAddress)}
                            />
                        </Bleed>
                    )}
                </Section>
            )}

            {/* 어떻게 알릴지 */}
            <Section divided>
                <SectionHeader title={t('flash.share.howTitle')} />
                <div className={styles.shareActions}>
                    <Button variant="primary" size="lg" block onClick={nativeShare}>
                        {t('flash.share.shareBtn')}
                    </Button>
                    <Button
                        variant="secondary"
                        size="lg"
                        block
                        onClick={() => setInviteOpen(true)}
                        disabled={!data?.flashId}
                    >
                        {t('meetingInvite.inviteFromPast')}
                    </Button>
                    <Button variant="ghost" size="lg" block onClick={copyLink}>
                        {t('flash.share.copyBtn')}
                    </Button>
                </div>
            </Section>

            {/* 초대한 사람 */}
            {invitedMembers.length > 0 && (
                <Section divided last>
                    <SectionHeader
                        title={t('flash.invitedParticipants')}
                        count={invitedUserNos.length}
                        sub={t('flash.invitedSent', { count: invitedUserNos.length })}
                    />
                    <ul className={styles.people}>
                        {invitedMembers.map(m => (
                            <li key={m.userNo} className={styles.person}>
                                <span className={styles.personAvatar}>{(m.nickname || '?').slice(0, 1)}</span>
                                <span className={styles.personName}>{m.nickname}</span>
                                <Badge tone="weak">{t('flash.invitedBadge')}</Badge>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {/* 지난 모임 멤버 고르기 — 고르면 번개 초대 푸시가 갑니다 */}
            {data?.flashId && (
                <InviteFromPastMeetings
                    open={inviteOpen}
                    onClose={() => setInviteOpen(false)}
                    targetMeetingId={data.flashId}
                    currentUserNo={userNo ? Number(userNo) : undefined}
                    titleStep1={t('flash.invitePickMeeting')}
                    titleStep2={t('flash.invitePickPeople')}
                    successToast={(n) => t('flash.flashInviteSent', { count: n })}
                    onInvite={async (userNos) => {
                        try {
                            const res: any = await FlashRepository.onPostInvite(data.flashId, { inviteeUserNos: userNos });
                            const httpStatus = res?.status;
                            const ok = httpStatus == null || (httpStatus >= 200 && httpStatus < 300);
                            if (ok) markOnboardingCompleted('step_invite');
                            return ok;
                        } catch {
                            return false;
                        }
                    }}
                />
            )}

            <ButtonFooter>
                <Button variant="ghost" size="lg" onClick={() => navigate('/home', { replace: true })}>
                    {t('flash.share.goHome')}
                </Button>
                {data && (
                    <Button
                        variant="primary"
                        size="lg"
                        block
                        onClick={() => navigate(`/flash/status/${data.flashId}`, { replace: true })}
                    >
                        {t('flash.share.viewStatus')}
                    </Button>
                )}
            </ButtonFooter>

            {toastMsg && <div className={styles.toast}>{toastMsg}</div>}
        </AppScreen>
    );
};
